import { sleep } from "@/lib/utils";

import type { Api, AuthUser } from "../types";
import { ApiError } from "../errors";
import { getAuthToken } from "../token";

// Offline principals, one per console persona. Signing in with a persona's
// email (any non-empty password) yields that persona's permission set, so the
// whole role matrix can be exercised without a backend or a seeded database.
//
// `admin` mirrors the seeded `admin@osmix.ru` account the gateway creates, so
// the mock and http paths sign in the same identity.
//
// NOTE: legacy `role: "admin"` is god-mode in `hasAdminPermission()` — it
// bypasses permission checks entirely. Every other persona keeps an ordinary
// `users.role` and gets console access from a `platformRole` instead, which is
// how real platform staff are modelled (see the `user_platform_roles` table);
// that way its `effectivePermissions` actually decide what it sees.

/** Pages every read-only observer persona can drill into (decision 2A). */
const OBSERVER_PAGES = [
	"console.access",
	"page.dashboard",
	"page.users",
	"page.orders",
	"page.shops",
	"page.products",
	"page.applications",
] as const;

const PRINCIPALS = {
	admin: {
		id: "usr_demo_admin",
		email: "admin@osmix.ru",
		phone: "+7 (999) 000-00-00",
		name: "Платформа",
		role: "admin",
		onboardingStatus: "active",
		effectivePermissions: [
			"console.access",
			"page.dashboard",
			"page.finances",
			"page.applications",
			"page.catalog_import",
			"action.catalog_import.run",
			"page.products",
			"page.categories",
			"page.ideas",
			"page.inspiration_feed",
			"page.orders",
			"page.carts",
			"page.shops",
			"page.users",
			"page.delivery",
			"page.geo",
			"page.platform_roles",
			"action.platform_roles.manage",
			"action.users.manage",
			"action.users.security",
		],
	},
	// The founder workspace: dashboard-first, full money visibility, and
	// drill-down into the operational pages — but no configuration surfaces
	// (roles, delivery, geo) and no moderation queues.
	founder: {
		id: "usr_demo_founder",
		email: "founder@osmix.ru",
		phone: "+7 (999) 000-00-01",
		name: "Основатель",
		role: "buyer",
		onboardingStatus: "active",
		platformRole: { key: "founder", name: "Основатель", system: false },
		effectivePermissions: [...OBSERVER_PAGES, "page.finances", "page.carts"],
	},
	// Money and growth, no operational detail.
	investor: {
		id: "usr_demo_investor",
		email: "investor@osmix.ru",
		phone: "+7 (999) 000-00-02",
		name: "Инвестор",
		role: "buyer",
		onboardingStatus: "active",
		platformRole: { key: "investor", name: "Инвестор", system: false },
		effectivePermissions: ["console.access", "page.dashboard", "page.finances"],
	},
	// Behavioural data, deliberately no finances.
	analyst: {
		id: "usr_demo_analyst",
		email: "analyst@osmix.ru",
		phone: "+7 (999) 000-00-03",
		name: "Аналитик",
		role: "buyer",
		onboardingStatus: "active",
		platformRole: { key: "analyst", name: "Аналитик", system: false },
		effectivePermissions: [...OBSERVER_PAGES, "page.carts"],
	},
} satisfies Record<string, AuthUser>;

export type MockPersona = keyof typeof PRINCIPALS;

/** Personas, for the login screen hint and for tests. */
export const MOCK_PERSONAS = Object.entries(PRINCIPALS).map(([key, user]) => ({
	key: key as MockPersona,
	email: user.email,
	name: user.name,
}));

// The token doubles as the persona handle so `me()` can restore the session
// after a reload without any server state.
const tokenFor = (persona: MockPersona) => `mock-token-${persona}`;

const personaFromToken = (token: string | null): MockPersona | null => {
	const key = token?.startsWith("mock-token-") ? token.slice("mock-token-".length) : null;
	return key && key in PRINCIPALS ? (key as MockPersona) : null;
};

export const auth: Api["auth"] = {
	async login(credentials) {
		await sleep(400);
		// Offline dev accepts any known persona email with any non-empty
		// password. The real gateway verifies the password hash; here we keep the
		// door open so UI work doesn't need a live backend.
		const email = credentials.email.trim().toLowerCase();
		const entry = Object.entries(PRINCIPALS).find(([, user]) => user.email === email);
		if (!entry || !credentials.password) {
			throw new ApiError("Invalid credentials", 401, "UNAUTHORIZED");
		}
		const [persona, user] = entry as [MockPersona, AuthUser];
		return { token: tokenFor(persona), user };
	},

	async me() {
		await sleep(150);
		const persona = personaFromToken(getAuthToken());
		if (!persona) throw new ApiError("Not authenticated", 401, "UNAUTHORIZED");
		return PRINCIPALS[persona];
	},

	async logout() {
		await sleep(100);
	},
};
